import { CSSProperties, HTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toSafeCssUrl } from '../utilities'
import { SubtleLoader } from './subtle-loader'

export interface StatefulImageProps extends HTMLAttributes<HTMLDivElement> {
	src?: string
	alt?: string

	aspectRatio?: number
	// force loading state
	forceIsLoading?: boolean
	isGenerating?: boolean

	loadingPlaceholder?: ReactNode
	errorPlaceholder?: ReactNode
	emptyPlaceholder?: ReactNode
	generatingPlaceholder?: ReactNode

	objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
	objectPosition?: string

	// show checkerboard (don't show this in simulators)
	showCheckerboard?: boolean
	onLoad?: () => void
	onError?: (e?: any) => void
}

/**
 * StatefulImage
 * A replacement for an <img /> that actually uses background-image.
 * - Keeps accessibility via role="img" + aria-label (from alt)
 * - Preloads the image and exposes loading, error, and empty fallbacks
 * - Supports aspect-ratio, cover/contain, transitions, and manual fallback toggles
 * - Supports explicit generating state (takes priority over loading/error/empty)
 */
export function StatefulImage({
	src,
	alt = '',
	aspectRatio,
	forceIsLoading = false,
	isGenerating = false,
	loadingPlaceholder,
	errorPlaceholder,
	emptyPlaceholder,
	generatingPlaceholder,
	objectFit = 'cover',
	objectPosition = 'center',
	showCheckerboard: showCheckerboardProp = false,
	onLoad,
	onError,
	className = '',
	style,
	...rest
}: StatefulImageProps) {
	const { t } = useTranslation('common')
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [hasError, setHasError] = useState<boolean>(false)
	const [currentSrc, setCurrentSrc] = useState<string | undefined>(src)
	const mounted = useRef(true)

	useEffect(() => {
		mounted.current = true
		return () => {
			mounted.current = false
		}
	}, [])

	// When src changes, start loading again
	useEffect(() => {
		if (!src) {
			setCurrentSrc(undefined)
			setIsLoading(false)
			setHasError(false)
			return
		}

		let img = new Image()
		img.loading = 'eager'
		setIsLoading(true)
		setHasError(false)

		const handleLoad = () => {
			if (!mounted.current) { return }
			setCurrentSrc(src)
			setIsLoading(false)
			setHasError(false)
			onLoad?.()
		}
		const handleError = (e?: any) => {
			if (!mounted.current) { return }
			setIsLoading(false)
			setHasError(true)
			onError?.(e)
		}

		img.addEventListener('load', handleLoad)
		img.addEventListener('error', handleError)
		img.src = src

		// in case browser has it cached and events already fired
		if (img.complete && img.naturalWidth) {
			handleLoad()
		}

		return () => {
			img.removeEventListener('load', handleLoad)
			img.removeEventListener('error', handleError)
			// allow GC
			img.src = ''
			img = null as any
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [src])

	const showEmpty = !isGenerating && !src
	const showLoading = !isGenerating && !!src && (forceIsLoading || isLoading)
	const showError = !isGenerating && !!src && !isLoading && hasError
	// a regeneration keeps the previous image visible, so the skeleton only covers an empty slot
	const showSkeleton = isGenerating && !currentSrc

	// container styles
	const bgStyles: CSSProperties = {
		backgroundImage: currentSrc ? toSafeCssUrl(currentSrc) ?? undefined : undefined,
		backgroundSize: objectFit,
		backgroundPosition: objectPosition,
		backgroundRepeat: 'no-repeat',
		transition: 'opacity 300ms ease, filter 300ms ease',
		// allow the user to override or extend via style prop
		...style,
	}

	// Aspect ratio spacer if requested
	const ratioPadding = typeof aspectRatio === 'number' && aspectRatio > 0
		? { paddingTop: `${100 / aspectRatio}%` }
		: undefined

	const dimmed = showLoading || showError || (isGenerating && !!currentSrc)

	return (
		<div
			{...rest}
			className={`relative overflow-hidden ${className}`}
			style={{
				backgroundColor: 'white',
				backgroundImage: showCheckerboardProp
					? 'linear-gradient(45deg, var(--color-grey-50) 25%, transparent 25%), linear-gradient(135deg, var(--color-grey-50) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, var(--color-grey-50) 75%), linear-gradient(135deg, transparent 75%, var(--color-grey-50) 75%)'
					: 'none',
				backgroundSize: '25px 25px',
				backgroundPosition: '0 0, 12.5px 0, 12.5px -12.5px, 0px 12.5px',
				...style,
			}}
			// role img and aria-label help screen readers understand this element as an image
			role={alt ? 'img' : undefined}
			aria-label={alt || undefined}
		>
			{/* spacer to preserve aspect ratio when provided */}
			{ratioPadding
				? (
					<div style={ratioPadding} aria-hidden>
						{/* positioned background */}
						<div
							style={bgStyles}
							className={`absolute inset-0 w-full h-full ${
								dimmed ? 'opacity-60 filter blur-sm' : 'opacity-100'
							}`}
						/>
					</div>
				)
				: (
					<div
						style={{ width: '100%', height: '100%', ...bgStyles }}
						className={`${dimmed ? 'opacity-60 filter blur-sm' : 'opacity-100'} w-full h-full`}
					/>
				)}

			{/* visually-hidden <img> helps some assistive tech and prefetching in some cases */}
			{src && (
				<img
					src={src}
					alt=""
					aria-hidden
					style={{ width: 0, height: 0, position: 'absolute', opacity: 0, pointerEvents: 'none' }}
				/>
			)}
			{showSkeleton && (
				<div
					role="status"
					aria-label={t('image.generating')}
					className="absolute inset-0 bg-grey-200 animate-pulse"
					style={{ pointerEvents: 'none' }}
					data-render-exclude
				>
					{generatingPlaceholder}
				</div>
			)}
			{isGenerating && !showSkeleton && (
				<div
					className="absolute inset-0 flex items-center justify-center p-2 animate-pulse bg-white/40"
					style={{ pointerEvents: 'none' }}
					data-render-exclude
				>
					{generatingPlaceholder ?? <div className="text-sm">{t('image.generating')}</div>}
				</div>
			)}

			{/* Loading fallback overlay */}
			{showLoading && (
				<div
					className="absolute inset-0 flex items-center justify-center p-2"
					style={{ pointerEvents: 'none' }}
					data-render-exclude
				>
					{loadingPlaceholder ?? (
						<div className="flex items-center gap-2 text-sm">
							<SubtleLoader width={32} delay={0} />
							<span className="sr-only">{t('image.loadingAlt')}</span>
						</div>
					)}
				</div>
			)}

			{/* Error fallback overlay */}
			{showError && (
				<div
					className="absolute inset-0 flex items-center justify-center p-2 text-red-600"
					style={{ pointerEvents: 'none' }}
					data-render-exclude
				>
					{errorPlaceholder ?? (
						<div className="text-xs text-center font-medium uppercase tracking-wider">
							{t('image.error')}
						</div>
					)}
				</div>
			)}

			{/* Empty fallback overlay */}
			{showEmpty && (
				<div
					className="absolute inset-0 flex items-center justify-center p-2 text-gray-500"
					style={{ pointerEvents: 'none' }}
					data-render-exclude
				>
					{emptyPlaceholder ?? (
						<div className="text-xs whitespace-nowrap opacity-50">{t('image.noImage')}</div>
					)}
				</div>
			)}
		</div>
	)
}
